import { useEffect } from 'react';
import authService from 'services/authService';

declare global {
  interface Window {
    clarity: any;
  }
}

/**
 * Internal helper to call Clarity API methods
 * This runs asynchronously in the background without blocking the UI
 * Returns a promise but should NOT be awaited in user-facing code
 */
const callClarity = (method: string, ...args: any[]): Promise<any> => {
  if (typeof window === 'undefined') {
    console.warn('Clarity: Window is undefined (SSR context)');
    return Promise.resolve(null);
  }

  // Fire-and-forget: Start the async operation but don't wait for it
  return new Promise((resolve) => {
    try {
      // Initialize clarity queue if not exists
      if (!window.clarity) {
        window.clarity = function() {
          (window.clarity.q = window.clarity.q || []).push(arguments);
        };
      }

      // Call clarity API - this runs in background
      Promise.resolve(window.clarity(method, ...args))
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          console.error(`Clarity API call failed for method "${method}":`, error);
          resolve(null);
        });
    } catch (error) {
      console.error(`Clarity API call failed for method "${method}":`, error);
      resolve(null);
    }
  });
};


/**
 * Identifies the current user in Microsoft Clarity
 * Sets user ID and role for session tracking and segmentation
 *
 * This function runs in the background and does NOT block the UI
 *
 * IMPORTANT: Only pass non-PII identifiers to Clarity
 * - Use hashed/obfuscated IDs if your user IDs contain PII
 * - Never pass email, phone, or other personally identifiable information
 */
export const identifyClarityUser = (): Promise<void> => {
  if (typeof window === 'undefined') {
    console.warn('Clarity: Cannot identify user in SSR context');
    return Promise.resolve();
  }

  // Return the promise to allow proper error handling
  return (async () => {
    try {
      const userData = authService.getUserData();
      const roles = authService.getUserRoles();

      if (userData?.id) {
        // Wait for identify to complete before setting additional properties
        const identifyResult = await callClarity("identify", userData.id.toString());

        if (identifyResult) {
          console.log('Microsoft Clarity: User identified successfully', {
            userId: userData.id,
            hashedId: identifyResult.id,
            sessionId: identifyResult.session,
            pageId: identifyResult.page
          });
        } else {
          console.log(`Microsoft Clarity: Identified user ${userData.id} (queued)`);
        }

        // Set role after identify completes
        if (roles.length > 0) {
          const roleValue = roles.sort().join(',');
          await callClarity("set", "role", roleValue);
          console.log(`Microsoft Clarity: Set user role to "${roleValue}"`);
        }
      } else {
        console.log('Microsoft Clarity: No user logged in, tracking anonymously');
      }
    } catch (error) {
      console.error('Microsoft Clarity: Error identifying user', error);
    }
  })();
};

/**
 * Send a custom event to Microsoft Clarity
 * Use this to track important user actions and business events
 *
 * This function runs in the background and does NOT block the UI
 * You can call it without await - it's fire-and-forget
 *
 * @param eventName - Name of the custom event (e.g., "purchase_completed", "button_clicked")
 * @param metadata - Optional metadata object to attach to the event
 *
 * @example
 * // Simple event - no await needed
 * clarityEvent("job_applied");
 *
 * @example
 * // Event with metadata
 * clarityEvent("course_purchased", {
 *   courseId: "123",
 *   price: "499",
 *   currency: "INR"
 * });
 *
 * @example
 * // Track user interactions
 * clarityEvent("resume_downloaded", {
 *   format: "pdf",
 *   template: "modern"
 * });
 */
export const clarityEvent = (eventName: string, metadata?: Record<string, string | number | boolean>): void => {
  if (typeof window === 'undefined') {
    console.warn('Clarity: Cannot send event in SSR context');
    return;
  }

  if (!eventName || typeof eventName !== 'string') {
    console.warn('Clarity: Event name must be a non-empty string');
    return;
  }

  // Run everything in background - don't block UI
  (async () => {
    try {
      // Get user roles to prefix the event name
      const roles = authService.getUserRoles();
      const rolePrefix = roles.length > 0 ? roles.sort().join(',') + '-' : '';
      const prefixedEventName = rolePrefix + eventName;

      // Send metadata to Clarity - wait for each to complete in background
      if (metadata && Object.keys(metadata).length > 0) {
        // Set each property as a custom tag sequentially
        for (const [key, value] of Object.entries(metadata)) {
          await callClarity("set", `${prefixedEventName}_${key}`, String(value));
        }
        console.log(`Microsoft Clarity: Set metadata for event "${prefixedEventName}":`, metadata);
      }

      // Track the event itself with role prefix after metadata is set
      await callClarity("event", prefixedEventName);
      console.log(`Microsoft Clarity: Event "${prefixedEventName}" sent successfully`);
    } catch (error) {
      console.error(`Microsoft Clarity: Error sending event "${eventName}"`, error);
    }
  })();
};

export const useClarity = () => {
  useEffect(() => {
    if (typeof window === 'undefined') {
      console.warn('Clarity: Window is undefined (SSR context)');
      return;
    }

    const clarityProjectId = process.env.REACT_APP_CLARITY_PROJECT_ID;

    if (!clarityProjectId) {
      console.warn('Microsoft Clarity: Project ID not found in environment variables (REACT_APP_CLARITY_PROJECT_ID)');
      return;
    }

    const existingScript = document.querySelector('script[src*="clarity.ms"]');
    if (existingScript) {
      console.log('Microsoft Clarity: Script already loaded, waiting for initialization...');

      // Wait for Clarity to be fully initialized before identifying user
      const initTimeout = setTimeout(() => {
        if (window.clarity) {
          identifyClarityUser().catch(error => {
            console.error('Microsoft Clarity: Error during user identification', error);
          });
        } else {
          console.warn('Microsoft Clarity: Script loaded but clarity object not available');
        }
      }, 2000); // Increased timeout to ensure Clarity is fully ready

      return () => clearTimeout(initTimeout);
    }

    // Initialize clarity queue before script loads
    window.clarity = window.clarity || function() {
      (window.clarity.q = window.clarity.q || []).push(arguments);
    };

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `https://www.clarity.ms/tag/${clarityProjectId}`;

    script.onerror = () => {
      console.error('Microsoft Clarity: Failed to load script from clarity.ms');
    };

    script.onload = () => {
      console.log('Microsoft Clarity: Script loaded successfully');

      // Wait a bit for Clarity to fully initialize after script load
      setTimeout(() => {
        identifyClarityUser().catch(error => {
            console.error('Microsoft Clarity: Error during user identification', error);
          });
      }, 1000); // Give Clarity time to initialize
    };

    document.head.appendChild(script);

    // Cleanup function
    return () => {
      // Note: We don't remove the script on unmount as Clarity should persist
      // across component lifecycles
    };
  }, []);
};